/**
 * VistoeraTASK-00638 — Member function authorization panel.
 * Shared by Team Management and Platform Admin (same API storage).
 */
import { Alert, Button, Input, Modal, Select, Space, Tag, Typography, message } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { AuthMemberMatrixResponse, AuthMatrixRow } from '../../types'
import MemberAuthMatrixTable from './MemberAuthMatrixTable'
import {
  countMemberOverrides,
  draftAllMemberInherit,
  draftFromSourceMemberRows,
  isMemberDirty,
  mapsFromMemberRows,
  memberDirtyEntries,
  type MemberDecision,
} from './memberAuthMatrixDraft'
import { grantKey } from './authMatrixDraft'

export type MemberAuthActor = {
  getMatrix: (workspaceId: string, userId: string) => Promise<{ data: AuthMemberMatrixResponse }>
  putGrant: (
    workspaceId: string,
    userId: string,
    body: { subject_kind: string; subject_id: string; decision: string },
  ) => Promise<unknown>
  resetInherit: (workspaceId: string, userId: string) => Promise<unknown>
  /** TASK-00671 — optional Team Feature Switch (Owner). */
  putTeamSwitch?: (
    workspaceId: string,
    body: { subject_kind: string; subject_id: string; decision: 'INHERIT' | 'DENY' },
  ) => Promise<unknown>
}

export type MemberPickerItem = {
  user_id: string
  display_name: string
  email: string
  role: string
  role_label?: string
  /** Cannot open this member in the picker at all. */
  disabled?: boolean
  disabledReason?: string
  /** TASK-00672 — Member Grant controls read-only (e.g. Owner self). Team switch may still edit. */
  memberGrantReadOnly?: boolean
}

type Props = {
  workspaceId: string
  planKey?: string | null
  planLabel?: string | null
  members: MemberPickerItem[]
  /** Pre-select a member (Platform Admin drawer). */
  initialUserId?: string | null
  actor: MemberAuthActor
  /** When true, hide the member carousel (single-member drawer). */
  hideMemberPicker?: boolean
  readOnly?: boolean
  /** TASK-00671 — Owner may toggle team-wide feature switches. */
  canManageTeamSwitch?: boolean
}

export default function MemberAuthMatrixPanel({
  workspaceId,
  planKey,
  planLabel,
  members,
  initialUserId,
  actor,
  hideMemberPicker,
  readOnly,
  canManageTeamSwitch,
}: Props) {
  const [q, setQ] = useState('')
  const [selectedUserId, setSelectedUserId] = useState<string | null>(initialUserId ?? null)
  const [matrix, setMatrix] = useState<AuthMemberMatrixResponse | null>(null)
  const [rows, setRows] = useState<AuthMatrixRow[]>([])
  const [baseline, setBaseline] = useState<Record<string, MemberDecision>>({})
  const [draft, setDraft] = useState<Record<string, MemberDecision>>({})
  const [workspaceEffective, setWorkspaceEffective] = useState<Record<string, boolean>>({})
  const [memberEffective, setMemberEffective] = useState<Record<string, boolean>>({})
  const [nodeIds, setNodeIds] = useState<string[]>([])
  const [channelIds, setChannelIds] = useState<string[]>([])
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [teamSwitchSavingKey, setTeamSwitchSavingKey] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const loadSeq = useRef(0)
  const draftRef = useRef(draft)
  const baselineRef = useRef(baseline)
  const selectedUserIdRef = useRef(selectedUserId)
  draftRef.current = draft
  baselineRef.current = baseline
  selectedUserIdRef.current = selectedUserId

  const dirty = useMemo(() => isMemberDirty(baseline, draft), [baseline, draft])
  const overrideStats = useMemo(
    () => countMemberOverrides(draft, nodeIds, channelIds),
    [draft, nodeIds, channelIds],
  )

  const filteredMembers = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return members
    return members.filter(
      (m) =>
        m.display_name.toLowerCase().includes(needle) ||
        m.email.toLowerCase().includes(needle) ||
        m.role.toLowerCase().includes(needle),
    )
  }, [members, q])

  const selectedMember = useMemo(
    () => members.find((m) => m.user_id === selectedUserId) ?? null,
    [members, selectedUserId],
  )

  const applyMatrix = useCallback((data: AuthMemberMatrixResponse, preserveSelection?: boolean) => {
    // Defense: never apply another workspace / user payload into this draft.
    if (data.workspace_id && data.workspace_id !== workspaceId) {
      setError('授权矩阵 workspace_id 与当前 Workspace 不匹配')
      return
    }
    if (selectedUserIdRef.current && data.user_id && data.user_id !== selectedUserIdRef.current) {
      setError('授权矩阵 user_id 与当前成员不匹配')
      return
    }
    setMatrix(data)
    setError(null)
    const maps = mapsFromMemberRows(data.rows || [])
    setRows(data.rows || [])
    setBaseline(maps.baseline)
    setDraft(maps.draft)
    setWorkspaceEffective(maps.workspaceEffective)
    setMemberEffective(maps.memberEffective)
    setNodeIds(maps.nodeIds)
    setChannelIds(maps.channelIds)
    setSelectedNodeId((prev) => {
      if (preserveSelection && prev && maps.nodeIds.includes(prev)) return prev
      return null
    })
  }, [workspaceId])

  const load = useCallback(
    (userId: string, preserveSelection?: boolean) => {
      if (!userId || !workspaceId) return
      const seq = ++loadSeq.current
      setLoading(true)
      setError(null)
      actor
        .getMatrix(workspaceId, userId)
        .then((r) => {
          if (seq !== loadSeq.current) return
          if (selectedUserIdRef.current !== userId) return
          applyMatrix(r.data, preserveSelection)
        })
        .catch((err) => {
          if (seq !== loadSeq.current) return
          setMatrix(null)
          setRows([])
          setError((err as Error).message || '加载成员授权矩阵失败')
        })
        .finally(() => {
          if (seq !== loadSeq.current) return
          setLoading(false)
        })
    },
    [actor, applyMatrix, workspaceId],
  )

  useEffect(() => {
    if (initialUserId) setSelectedUserId(initialUserId)
  }, [initialUserId])

  // Prefer a grant-editable ordinary member so Owner doesn't land on self-bypass view.
  useEffect(() => {
    if (initialUserId || selectedUserId) return
    if (!members.length) return
    const editable = members.find((m) => !m.disabled && !m.memberGrantReadOnly && m.role !== 'owner')
    const fallback = members.find((m) => !m.disabled)
    const pick = editable ?? fallback
    if (pick) setSelectedUserId(pick.user_id)
  }, [initialUserId, members, selectedUserId])

  useEffect(() => {
    if (!selectedUserId) {
      setMatrix(null)
      setRows([])
      return
    }
    load(selectedUserId)
  }, [load, selectedUserId])

  useEffect(() => {
    if (!dirty) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  const grantReadOnly = Boolean(readOnly || selectedMember?.disabled || selectedMember?.memberGrantReadOnly)
  const isOwnerTarget = selectedMember?.role === 'owner'

  const requestSelectMember = (nextId: string) => {
    if (nextId === selectedUserId) return
    const target = members.find((m) => m.user_id === nextId)
    if (target?.disabled) {
      message.warning(target.disabledReason || '当前账号不能管理该成员的功能授权')
      return
    }
    if (!dirty) {
      setSelectedUserId(nextId)
      return
    }
    Modal.confirm({
      title: '有未保存的成员授权修改',
      content: '切换成员将丢弃当前 Draft。是否放弃修改并切换？',
      okText: '放弃并切换',
      cancelText: '取消',
      onOk: () => setSelectedUserId(nextId),
    })
  }

  const onDecisionChange = (kind: 'node' | 'channel', subjectId: string, decision: MemberDecision) => {
    const key = grantKey(kind, subjectId)
    if (decision === 'ALLOW' && workspaceEffective[key] === false) {
      message.warning('成员 ALLOW 不能突破 Workspace 明确禁止')
      return
    }
    const row = rows.find((r) =>
      kind === 'node' ? r.node.subject_id === subjectId : r.channels.some((c) => c.subject_id === subjectId),
    )
    const teamClosed =
      kind === 'node'
        ? Boolean((row?.node as { team_closed?: boolean } | undefined)?.team_closed)
        : Boolean(
            (row?.channels.find((c) => c.subject_id === subjectId) as { team_closed?: boolean } | undefined)
              ?.team_closed,
          )
    if (decision === 'ALLOW' && teamClosed) {
      message.warning('成员 ALLOW 不能突破团队已关闭的功能')
      return
    }
    setDraft((prev) => ({ ...prev, [key]: decision }))
  }

  const onTeamSwitchChange = useCallback(
    async (kind: 'node' | 'channel', subjectId: string, decision: 'INHERIT' | 'DENY') => {
      if (!canManageTeamSwitch || !actor.putTeamSwitch) return
      const userId = selectedUserIdRef.current
      if (!userId) return
      const switchKey = `${kind}:${subjectId}`
      setTeamSwitchSavingKey(switchKey)
      try {
        await actor.putTeamSwitch(workspaceId, {
          subject_kind: kind,
          subject_id: subjectId,
          decision,
        })
        const verified = await actor.getMatrix(workspaceId, userId)
        if (selectedUserIdRef.current !== userId) return
        applyMatrix(verified.data, true)
        message.success(decision === 'DENY' ? '已关闭团队功能' : '已恢复团队功能')
      } catch (err) {
        message.error(`团队开关保存失败：${(err as Error).message}`)
      } finally {
        setTeamSwitchSavingKey(null)
      }
    },
    [actor, applyMatrix, canManageTeamSwitch, workspaceId],
  )

  const save = useCallback(async (): Promise<boolean> => {
    const userId = selectedUserIdRef.current
    if (!userId) return false
    const changes = memberDirtyEntries(baselineRef.current, draftRef.current)
    if (changes.length === 0) {
      message.info('没有需要保存的修改')
      return true
    }
    setSaving(true)
    const failures: string[] = []
    try {
      for (const change of changes) {
        try {
          await actor.putGrant(workspaceId, userId, {
            subject_kind: change.kind,
            subject_id: change.subjectId,
            decision: change.decision,
          })
        } catch (err) {
          failures.push(`${change.kind}:${change.subjectId} — ${(err as Error).message}`)
        }
      }
      const verified = await actor.getMatrix(workspaceId, userId)
      if (selectedUserIdRef.current !== userId) return false
      applyMatrix(verified.data, true)
      if (failures.length > 0) {
        message.error(`部分保存失败（${failures.length}/${changes.length}）：${failures[0]}`)
        return false
      }
      message.success(`已保存 ${changes.length} 项成员授权`)
      return true
    } catch (err) {
      message.error(`保存失败：${(err as Error).message}`)
      return false
    } finally {
      setSaving(false)
    }
  }, [actor, applyMatrix, workspaceId])

  const requestResetInherit = () => {
    const userId = selectedUserId
    if (!userId) return
    Modal.confirm({
      title: '恢复继承团队？',
      content:
        '将把当前成员的节点及通道显式授权全部恢复为「继承团队」。不会修改团队统一开关、套餐、Workspace Override、其他成员、团队角色或积分。',
      okText: '确认恢复',
      cancelText: '取消',
      onOk: async () => {
        setSaving(true)
        try {
          await actor.resetInherit(workspaceId, userId)
          const verified = await actor.getMatrix(workspaceId, userId)
          applyMatrix(verified.data, true)
          message.success('已恢复为继承团队')
        } catch (err) {
          message.error(`恢复失败：${(err as Error).message}`)
        } finally {
          setSaving(false)
        }
      },
    })
  }

  const requestCopyFromMember = () => {
    const userId = selectedUserId
    if (!userId) return
    const sources = members.filter((m) => m.user_id !== userId && !m.disabled)
    if (sources.length === 0) {
      message.info('没有可复制的同 Workspace 成员')
      return
    }
    let sourceId = sources[0].user_id
    Modal.confirm({
      title: '复制其他成员权限',
      content: (
        <div>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            仅复制显式 Member Grant（节点/通道）。不复制角色、Owner 身份、Workspace/套餐授权、积分或资产。
          </Typography.Paragraph>
          <Select
            style={{ width: '100%' }}
            defaultValue={sourceId}
            options={sources.map((m) => ({
              value: m.user_id,
              label: `${m.display_name}（${m.role_label || m.role}）· ${m.email}`,
            }))}
            onChange={(v) => {
              sourceId = v
            }}
          />
        </div>
      ),
      okText: '复制到当前 Draft',
      cancelText: '取消',
      onOk: async () => {
        try {
          const src = await actor.getMatrix(workspaceId, sourceId)
          if (src.data.workspace_id !== workspaceId) {
            message.error('源成员不属于当前 Workspace')
            return
          }
          const copied = draftFromSourceMemberRows(src.data.rows || [])
          // Ensure every subject key exists so dirty detection covers clears→INHERIT.
          const next = draftAllMemberInherit(nodeIds, channelIds)
          Object.assign(next, copied)
          setDraft(next)
          message.success('已复制到 Draft，请确认后保存')
        } catch (err) {
          message.error(`复制失败：${(err as Error).message}`)
        }
      },
    })
  }

  const planText = planLabel || planKey || matrix?.plan_key || '—'

  return (
    <div data-testid="member-auth-matrix-panel">
      {error ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={error}
          action={
            selectedUserId ? (
              <Button size="small" onClick={() => load(selectedUserId)}>
                重试
              </Button>
            ) : null
          }
        />
      ) : null}

      {!hideMemberPicker ? (
        <div className="tm-card" style={{ marginBottom: 12, padding: 12 }}>
          <Input
            allowClear
            placeholder="搜索成员姓名或邮箱…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ maxWidth: 320, marginBottom: 10 }}
            data-testid="member-auth-search"
          />
          <div
            style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}
            data-testid="member-auth-carousel"
          >
            {filteredMembers.map((m) => {
              const active = m.user_id === selectedUserId
              return (
                <button
                  key={m.user_id}
                  type="button"
                  disabled={m.disabled && !active}
                  onClick={() => requestSelectMember(m.user_id)}
                  className={`tm-seg-item${active ? ' is-active' : ''}`}
                  style={{
                    whiteSpace: 'nowrap',
                    opacity: m.disabled && !active ? 0.45 : 1,
                    cursor: m.disabled && !active ? 'not-allowed' : 'pointer',
                  }}
                  title={m.disabledReason || m.email}
                  data-testid={`member-auth-pick-${m.user_id}`}
                >
                  {m.display_name}
                </button>
              )
            })}
            {filteredMembers.length === 0 ? (
              <Typography.Text type="secondary">没有匹配的成员</Typography.Text>
            ) : null}
          </div>
        </div>
      ) : null}

      {selectedMember ? (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            alignItems: 'center',
            marginBottom: 12,
            justifyContent: 'space-between',
          }}
        >
          <Space wrap>
            <Typography.Text strong>
              {selectedMember.display_name}{' '}
              <Typography.Text type="secondary">{selectedMember.email}</Typography.Text>
            </Typography.Text>
            <Tag color="blue">{selectedMember.role_label || selectedMember.role}</Tag>
            <Tag>基于团队套餐：{planText}</Tag>
            {dirty ? <Tag color="orange">未保存</Tag> : <Tag>已同步</Tag>}
            <Tag>
              成员覆盖 节点 {overrideStats.nodes} / 通道 {overrideStats.channels}
            </Tag>
            {matrix?.enforcement_mode ? <Tag>模式 {matrix.enforcement_mode}</Tag> : null}
          </Space>
          <Space wrap>
            <Button disabled={grantReadOnly || saving || loading || !selectedUserId} onClick={requestCopyFromMember}>
              复制其他成员权限
            </Button>
            <Button disabled={grantReadOnly || saving || loading || !selectedUserId} onClick={requestResetInherit}>
              恢复继承团队
            </Button>
            <Button
              disabled={!dirty || saving}
              onClick={() => selectedUserId && load(selectedUserId)}
            >
              放弃修改
            </Button>
            <Button type="primary" loading={saving} disabled={grantReadOnly || !dirty} onClick={() => void save()}>
              保存更改
            </Button>
          </Space>
        </div>
      ) : (
        <Alert type="info" showIcon message="请选择一位成员以配置功能授权" style={{ marginBottom: 12 }} />
      )}

      {isOwnerTarget ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="团队所有者：拥有 Workspace 完整功能使用权"
          description="Owner 不受普通 Member Grant 限制。团队统一开关可在此管理；请切换到其他成员设置独立节点/通道授权。"
        />
      ) : null}

      {selectedUserId ? (
        <MemberAuthMatrixTable
          rows={rows}
          draft={draft}
          baseline={baseline}
          workspaceEffective={workspaceEffective}
          memberEffective={memberEffective}
          selectedNodeId={selectedNodeId}
          onSelectNode={setSelectedNodeId}
          onDecisionChange={onDecisionChange}
          canManageTeamSwitch={!!canManageTeamSwitch && !!actor.putTeamSwitch}
          onTeamSwitchChange={onTeamSwitchChange}
          teamSwitchSavingKey={teamSwitchSavingKey}
          loading={loading}
          readOnly={grantReadOnly}
        />
      ) : null}
    </div>
  )
}
